import { supabase } from "@/integrations/supabase/client";

export type StorefrontBrandVariant = "default" | "motors";

export interface StorefrontThemeConfig {
  brandVariant?: StorefrontBrandVariant;
  primaryColor?: string;
  accentColor?: string;
  headingFont?: string;
  bodyFont?: string;
  cardRadius?: number;
  navigationStyle?: string;
  heroBadge?: string;
  heroTitle?: string;
  heroLineHighlight?: string;
  heroSubtitle?: string;
  heroCtaPrimary?: string;
  heroCtaSecondary?: string;
  featureTitles?: string[];
  footerBlurb?: string;
  contactEmailOverride?: string;
  contactLocationOverride?: string;
}

export interface StorefrontSectionRow {
  id: string;
  type: string;
  title: string;
  active: boolean;
  sortOrder: number;
}

export interface StorefrontWebSettingsRow {
  storefront_online: boolean;
  storefront_maintenance: boolean;
  storefront_maintenance_message: string;
  storefront_password_enabled: boolean;
  storefront_allow_search_indexing: boolean;
  storefront_theme: StorefrontThemeConfig;
  storefront_sections: StorefrontSectionRow[];
  storefront_custom_domain: string;
  store_name: string;
  store_email: string;
  store_phone: string;
  store_website: string;
  store_address: string;
  store_description: string;
  updated_at?: string;
}

const THEME_SELECT = `
  storefront_online,
  storefront_maintenance,
  storefront_maintenance_message,
  storefront_password_enabled,
  storefront_allow_search_indexing,
  storefront_theme,
  storefront_sections,
  storefront_custom_domain,
  store_name,
  store_email,
  store_phone,
  store_website,
  store_address,
  store_description,
  updated_at
`;

/** Default homepage sections — automotive / EV dealer & manufacturing context */
export const DEFAULT_SECTIONS: StorefrontSectionRow[] = [
  { id: "hero", type: "Hero", title: "EV & vehicles hero", active: true, sortOrder: 0 },
  { id: "features", type: "Features", title: "Service, parts & charging", active: true, sortOrder: 1 },
  { id: "products", type: "Featured Products", title: "Parts, accessories & merch", active: true, sortOrder: 2 },
  { id: "footer", type: "Footer", title: "Dealer & manufacturing footer", active: true, sortOrder: 3 },
];

/** Suggested theme for EV OEM / dealer storefronts (e.g. Kalki Motors) */
export const AUTOMOTIVE_EV_DEFAULT_THEME: StorefrontThemeConfig = {
  brandVariant: "motors",
  primaryColor: "#0c1222",
  accentColor: "#e11d48",
  heroBadge: "Electric vehicles · Manufacturing · After-sales",
  heroTitle: "Electric mobility",
  heroLineHighlight: "starts here",
  heroSubtitle:
    "Explore EV models, genuine OEM parts, charging solutions, and accessories from our manufacturing and authorised network.",
  heroCtaPrimary: "Browse vehicles & parts",
  heroCtaSecondary: "Book service / test drive",
  featureTitles: [
    "Certified EV service",
    "Genuine OEM parts",
    "Charging & battery care",
    "Finance, fleet & warranty",
  ],
  footerBlurb:
    "We design and build electric vehicles and support every journey with genuine parts, trained technicians, and nationwide service.",
};

export function normalizeSections(raw: unknown): StorefrontSectionRow[] {
  if (!Array.isArray(raw) || raw.length === 0) return [...DEFAULT_SECTIONS];
  return raw
    .map((r: any, i: number) => ({
      id: String(r.id ?? `sec-${i}`),
      type: String(r.type ?? "Block"),
      title: String(r.title ?? ""),
      active: r.active !== false,
      sortOrder: typeof r.sortOrder === "number" ? r.sortOrder : i,
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

export type ResolvedStorefrontTheme = ReturnType<typeof mergeStorefrontTheme>;

export function mergeStorefrontTheme(
  row: Pick<
    StorefrontWebSettingsRow,
    | "store_name"
    | "store_email"
    | "store_address"
    | "store_description"
    | "store_website"
    | "store_phone"
  >,
  theme: StorefrontThemeConfig | null | undefined,
) {
  const t = theme ?? {};
  const name = (row.store_name || "Store").trim() || "Store";
  const isMotors = t.brandVariant === "motors";
  const defaultFeatures = isMotors
    ? [
        "Certified EV service",
        "Genuine OEM parts",
        "Charging & battery care",
        "Finance, fleet & warranty",
      ]
    : ["Free shipping", "Easy returns", "Secure payment", "Flexible pay"];

  const heroTitle = (t.heroTitle || "").trim() || (isMotors ? "Electric mobility" : `Shop ${name}`);
  const highlight = (t.heroLineHighlight || "").trim() || (isMotors ? "starts here" : "style");
  const heroBadge =
    (t.heroBadge || "").trim() ||
    (row.store_description
      ? row.store_description.slice(0, 80)
      : isMotors
        ? `${name} — EV manufacturing & dealer network`
        : `${name} — online store`);
  const heroSubtitle =
    (t.heroSubtitle || "").trim() ||
    row.store_description ||
    (isMotors
      ? "EV models, genuine parts, charging solutions, and accessories — backed by manufacturing and authorised service."
      : "Browse our catalogue and order with secure checkout.");

  return {
    brandVariant: (t.brandVariant === "motors" ? "motors" : "default") as StorefrontBrandVariant,
    primaryColor: t.primaryColor || "#0f172a",
    accentColor: t.accentColor || (isMotors ? "#dc2626" : "#4f46e5"),
    headingFont: t.headingFont || "Inter Tight",
    bodyFont: t.bodyFont || "Inter",
    cardRadius: typeof t.cardRadius === "number" ? t.cardRadius : 30,
    navigationStyle: t.navigationStyle || "minimal",
    storeDisplayName: name,
    storeTagline: row.store_website || "",
    heroBadge,
    heroTitle,
    heroLineHighlight: highlight,
    heroSubtitle,
    heroCtaPrimary: (t.heroCtaPrimary || "").trim() || (isMotors ? "Browse catalogue" : "Shop now"),
    heroCtaSecondary: (t.heroCtaSecondary || "").trim() || (isMotors ? "Contact us" : "Learn more"),
    featureTitles: Array.isArray(t.featureTitles) && t.featureTitles.length >= 4
      ? t.featureTitles.slice(0, 4)
      : defaultFeatures,
    footerBlurb:
      (t.footerBlurb || "").trim() ||
      row.store_description ||
      (isMotors
        ? `${name} — electric vehicles, genuine parts, charging solutions, and authorised service.`
        : `Quality products from ${name}.`),
    contactEmail: (t.contactEmailOverride || row.store_email || "").trim() || "—",
    contactLocation: (t.contactLocationOverride || row.store_address || "").trim() || "—",
    contactPhone: (row.store_phone || "").trim(),
  };
}

export async function fetchStorefrontWebSettings(organizationId: string): Promise<StorefrontWebSettingsRow | null> {
  if (!organizationId) return null;
  const { data, error } = await supabase
    .from("ecommerce_settings" as any)
    .select(THEME_SELECT)
    .eq("organization_id", organizationId)
    .maybeSingle();
  if (error) {
    console.error("fetchStorefrontWebSettings", error);
    return null;
  }
  if (!data) return null;
  const d = data as any;
  return {
    storefront_online: d.storefront_online !== false,
    storefront_maintenance: !!d.storefront_maintenance,
    storefront_maintenance_message: String(d.storefront_maintenance_message || ""),
    storefront_password_enabled: !!d.storefront_password_enabled,
    storefront_allow_search_indexing: d.storefront_allow_search_indexing !== false,
    storefront_theme: (d.storefront_theme || {}) as StorefrontThemeConfig,
    storefront_sections: normalizeSections(d.storefront_sections),
    storefront_custom_domain: String(d.storefront_custom_domain || ""),
    store_name: String(d.store_name || ""),
    store_email: String(d.store_email || ""),
    store_phone: String(d.store_phone || ""),
    store_website: String(d.store_website || ""),
    store_address: String(d.store_address || ""),
    store_description: String(d.store_description || ""),
    updated_at: d.updated_at,
  };
}

export type StorefrontWebPatch = Partial<{
  storefront_online: boolean;
  storefront_maintenance: boolean;
  storefront_maintenance_message: string;
  storefront_password_enabled: boolean;
  storefront_allow_search_indexing: boolean;
  storefront_theme: StorefrontThemeConfig;
  storefront_sections: StorefrontSectionRow[];
  storefront_custom_domain: string;
  store_name: string;
}>;

export async function upsertStorefrontWebSettings(
  organizationId: string,
  patch: StorefrontWebPatch,
  existingRowId?: string | null,
): Promise<{ error: Error | null }> {
  const { storefront_theme, storefront_sections, ...scalarPatch } = patch;
  const row: Record<string, unknown> = {
    ...scalarPatch,
    updated_at: new Date().toISOString(),
  };
  if (storefront_theme !== undefined) row.storefront_theme = storefront_theme;
  if (storefront_sections !== undefined) row.storefront_sections = storefront_sections;

  if (existingRowId) {
    const { error } = await supabase.from("ecommerce_settings" as any).update(row).eq("id", existingRowId);
    return { error: error ? new Error(error.message) : null };
  }

  const insertPayload: Record<string, unknown> = {
    organization_id: organizationId,
    store_name: patch.store_name ?? "Online store",
    ...row,
  };
  const { error } = await supabase.from("ecommerce_settings" as any).insert(insertPayload);
  return { error: error ? new Error(error.message) : null };
}

export async function fetchEcommerceSettingsRowId(organizationId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from("ecommerce_settings" as any)
    .select("id")
    .eq("organization_id", organizationId)
    .maybeSingle();
  if (error || !data) return null;
  return (data as { id: string }).id;
}

export async function verifyStorefrontGate(password: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("ecommerce_verify_storefront_gate" as any, {
    p_password: password,
  });
  if (error) {
    console.error("verifyStorefrontGate", error);
    return false;
  }
  return data === true;
}

/**
 * Anon-callable storefront gate verification. Used by the public storefront
 * (separate Vite target), where there is no JWT-bound `organization_id`. The
 * RPC is parameterised by `organization_id` resolved from the request host,
 * never returns the stored hash, and is granted EXECUTE to `anon`.
 */
export async function verifyStorefrontGatePublic(
  organizationId: string,
  password: string,
): Promise<boolean> {
  if (!organizationId || !password) return false;
  const { data, error } = await supabase.rpc("ecommerce_verify_storefront_gate_public", {
    p_organization_id: organizationId,
    p_password: password,
  });
  if (error) {
    console.error("verifyStorefrontGatePublic", error);
    return false;
  }
  return data === true;
}

export async function setStorefrontGatePassword(newPassword: string): Promise<{ error: string | null }> {
  const { error } = await supabase.rpc("ecommerce_set_storefront_gate_password" as any, {
    p_new_password: newPassword,
  });
  return { error: error?.message ?? null };
}

export interface StorefrontStats {
  ordersLast7Days: number;
  publishedProducts: number;
  activeCategories: number;
}

export async function fetchStorefrontStats(organizationId: string): Promise<StorefrontStats> {
  const since = new Date();
  since.setDate(since.getDate() - 7);

  const [ordersRes, productsRes, catsRes] = await Promise.all([
    supabase
      .from("sales_orders")
      .select("id", { count: "exact", head: true })
      .eq("organization_id", organizationId)
      .eq("sales_channel", "online")
      .eq("order_source", "storefront")
      .gte("created_at", since.toISOString()),
    supabase
      .from("ecommerce_products" as any)
      .select("id", { count: "exact", head: true })
      .eq("organization_id", organizationId)
      .eq("status", "active")
      .eq("is_published", true)
      .eq("is_deleted", false),
    supabase
      .from("ecommerce_categories" as any)
      .select("id", { count: "exact", head: true })
      .eq("organization_id", organizationId)
      .eq("is_active", true),
  ]);

  return {
    ordersLast7Days: ordersRes.count ?? 0,
    publishedProducts: productsRes.count ?? 0,
    activeCategories: catsRes.count ?? 0,
  };
}

export function gateSessionKey(organizationId: string) {
  return `sbms_storefront_gate_${organizationId}`;
}

export function isStorefrontGateUnlocked(organizationId: string): boolean {
  try {
    return sessionStorage.getItem(gateSessionKey(organizationId)) === "1";
  } catch {
    return false;
  }
}

export function unlockStorefrontGate(organizationId: string) {
  try {
    sessionStorage.setItem(gateSessionKey(organizationId), "1");
  } catch {
    /* ignore */
  }
}
